'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';

import {
  loginUser as loginUserAction,
  registerUser as registerUserAction,
  logoutUser as logoutUserAction,
} from '@/lib/actions/auth.actions';

import type { User } from '@/lib/types/auth.types';
import { notify } from '@/lib/notification/toast-manager';
import {
  useOptionalSession,
  type SessionContextValue,
} from '@/providers/SessionProvider';

/* =============================================================================
 * TYPES
 * ============================================================================= */

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterCredentials {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  password_confirmation: string;
  phone: string;
  user_type: 'customer' | 'owner' | 'waiter' | 'admin';
  terms_accepted: boolean;
}

export interface LogoutOptions {
  redirectTo?: string;
  silent?: boolean;
  reason?: string;
}

export interface UserContextValue {
  // User state
  user: User | null;
  isLoading: boolean;
  isInitializing: boolean;
  isAuthenticated: boolean;
  error: string | null;

  // Auth operations
  registerUser: (credentials: RegisterCredentials) => Promise<boolean>;
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: (options?: LogoutOptions) => Promise<void>;

  // State management
  hydrateUser: (nextUser: User | null, opts?: { sessionValid?: boolean }) => void;
  clearError: () => void;
  invalidateSession: (reason?: string) => void;

  // Navigation helpers
  getDashboardPath: (userData: User) => string;
}

/* =============================================================================
 * CONFIG
 * ============================================================================= */

const DEV = process.env.NODE_ENV === 'development';
const TAG = '[CLIENT][UserContext]';

/* =============================================================================
 * CONTEXT
 * ============================================================================= */

const UserContext = createContext<UserContextValue | undefined>(undefined);

/* =============================================================================
 * INTERNAL HELPERS
 * ============================================================================= */

function safeJson(payload: unknown) {
  try {
    return payload ? JSON.parse(JSON.stringify(payload)) : undefined;
  } catch {
    return undefined;
  }
}

function dlog(event: string, payload?: Record<string, unknown>) {
  if (!DEV) return;
  console.log(`${TAG} ${event}`, payload ? safeJson(payload) : '');
}

function dwarn(event: string, payload?: Record<string, unknown>) {
  if (!DEV) return;
  console.warn(`${TAG} ⚠️ ${event}`, payload ? safeJson(payload) : '');
}

function derr(event: string, payload?: Record<string, unknown>) {
  console.error(`${TAG} ❌ ${event}`, payload ? safeJson(payload) : '');
}

function newOpId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/* =============================================================================
 * PROVIDER
 * ============================================================================= */

export interface UserProviderProps {
  children: ReactNode;
  initialUser: User | null;
  initialSessionValid?: boolean;
}

export function UserProvider({
  children,
  initialUser,
  initialSessionValid = !!initialUser,
}: UserProviderProps) {
  const router = useRouter();

  const sessionCtx = useOptionalSession() as SessionContextValue | undefined;

  // State management
  const [user, setUser] = useState<User | null>(initialUser);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false); // Start false - optimistic
  const [error, setError] = useState<string | null>(null);

  // Session validity comes from SessionProvider or defaults to initial state
  const sessionValid = sessionCtx?.sessionValid ?? initialSessionValid;

  // ✅ CRITICAL FIX: isAuthenticated = sessionValid (NOT !!user && sessionValid)
  const isAuthenticated = sessionValid;

  /* ---------------------------------------------------------------------------
   * HYDRATE USER
   * ------------------------------------------------------------------------- */
  const hydrateUser = useCallback(
    (nextUser: User | null, opts?: { sessionValid?: boolean }) => {
      setUser(nextUser);
      setError(null);

      dlog('hydrateUser', {
        hasUser: Boolean(nextUser),
        sessionValid: opts?.sessionValid ?? !!nextUser,
      });
    },
    []
  );

  /* ---------------------------------------------------------------------------
   * INVALIDATE SESSION
   * ------------------------------------------------------------------------- */
  const invalidateSession = useCallback(
    (reason?: string) => {
      if (sessionCtx) {
        sessionCtx.invalidate(reason || 'user_context_invalidate');
      }
      
      dwarn('invalidateSession', { reason: reason || 'user_context_invalidate' });
    },
    [sessionCtx]
  );

  /* ---------------------------------------------------------------------------
   * REGISTER
   * ------------------------------------------------------------------------- */
  const registerUser = useCallback(
    async (credentials: RegisterCredentials): Promise<boolean> => {
      const opId = newOpId('register');
      dlog('register:start', { opId, email: String(credentials.email || '').toLowerCase() });

      setIsLoading(true);
      setError(null);

      try {
        const result = await registerUserAction(credentials);

        if (result.status !== 'success' || !result.data?.user) {
          const msg = result.message || 'Registration failed';
          setError(msg);
          notify.error(`${msg} ❌`);
          dwarn('register:failed', { opId, msg, status: result.status });
          return false;
        }

        hydrateUser(result.data.user, { sessionValid: true });

        notify.success('🎉 Registration successful! Welcome to IndeMenu');

        // Clean redirect without forcing refresh
        if (result.data.redirectTo) {
          router.replace(result.data.redirectTo);
        } else {
          const dashboardPath = getDashboardPath(result.data.user);
          router.replace(dashboardPath);
        }

        dlog('register:success', { opId, redirectTo: result.data.redirectTo });
        return true;
      } catch (e: any) {
        const msg = e?.message || 'Registration failed unexpectedly';
        setError(msg);
        notify.error(`${msg} ❌`);
        derr('register:error', { opId, msg });
        return false;
      } finally {
        setIsLoading(false);
        dlog('register:done', { opId });
      }
    },
    [hydrateUser, router]
  );

  /* ---------------------------------------------------------------------------
   * LOGIN
   * ------------------------------------------------------------------------- */
  const login = useCallback(
    async (credentials: LoginCredentials): Promise<boolean> => {
      const opId = newOpId('login');
      dlog('login:start', { opId, email: String(credentials.email || '').toLowerCase() });

      setIsLoading(true);
      setError(null);

      try {
        const result = await loginUserAction(credentials);

        if (result.status !== 'success' || !result.data?.user) {
          const msg = result.message || 'Login failed';
          setError(msg);
          notify.error(`${msg} ❌`);
          dwarn('login:failed', { opId, msg, status: result.status });
          return false;
        }

        hydrateUser(result.data.user, { sessionValid: true });

        notify.success('✅ Login successful');

        if (result.data.redirectTo) {
          router.replace(result.data.redirectTo);
        } else {
          router.refresh(); // Let server components handle the redirect
        }

        dlog('login:success', { opId, redirectTo: result.data.redirectTo || '(refresh)' });
        return true;
      } catch (e: any) {
        const msg = e?.message || 'Login failed unexpectedly';
        setError(msg);
        notify.error(`${msg} ❌`);
        derr('login:error', { opId, msg });
        return false;
      } finally {
        setIsLoading(false);
        dlog('login:done', { opId });
      }
    },
    [hydrateUser, router]
  );

  /* ---------------------------------------------------------------------------
   * LOGOUT
   * ------------------------------------------------------------------------- */
  const logout = useCallback(
    async (options: LogoutOptions = {}): Promise<void> => {
      const {
        redirectTo = '/login',
        silent = false,
        reason = 'user_initiated',
      } = options;

      const opId = newOpId('logout');
      dlog('logout:start', { opId, redirectTo, silent, reason });

      try {
        if (!silent) setIsLoading(true);

        await logoutUserAction();

        hydrateUser(null, { sessionValid: false });
        invalidateSession(reason || 'logout');

        if (!silent) notify.info('👋 Logged out successfully');

        // Clean redirect
        router.replace(`${redirectTo}?logout=${reason}`);

        dlog('logout:success', { opId, to: redirectTo });
      } catch (e: any) {
        if (!silent) notify.error(e?.message || 'Failed to log out');
        derr('logout:error', { opId, message: String(e?.message ?? e) });
      } finally {
        if (!silent) setIsLoading(false);
        dlog('logout:done', { opId });
      }
    },
    [hydrateUser, invalidateSession, router]
  );

  // Helper function to get dashboard path based on user type
  const getDashboardPath = useCallback((userData: User): string => {
    const userType = userData.user_type;
    switch (userType) {
      case 'owner':
        return '/dashboards/vendor';
      case 'waiter':
        return '/dashboards/waiter';
      case 'admin':
        return '/dashboards/admin';
      case 'customer':
      default:
        return '/dashboards/customer';
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<UserContextValue>(
    () => ({
      user,
      isLoading,
      isInitializing,
      isAuthenticated, // ✅ Uses sessionValid, not !!user && sessionValid
      error,

      // Auth operations
      registerUser,
      login,
      logout,

      // State management
      hydrateUser,
      clearError,
      invalidateSession,

      // Navigation helpers
      getDashboardPath,
    }),
    [
      user,
      isLoading,
      isInitializing,
      isAuthenticated,
      error,
      registerUser,
      login,
      logout,
      hydrateUser,
      clearError,
      invalidateSession,
      getDashboardPath,
    ]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

/* =============================================================================
 * HOOK
 * ============================================================================= */

export function useUser(): UserContextValue {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser must be used within a UserProvider');
  return ctx;
}